import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { BehaviorSubject, combineLatest, finalize, map, Observable, of, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContactsService } from '../../../../../contacts/services/contacts.service';
import { Contact } from '../../../../../contacts/models/contacts';
import { OrgPersonAssignment } from '../../../../models/organisation-detail';
import { resolveEffectiveVisibility } from '../../../../utils/organisations.util';
import { IsActiveComponent } from '../../../../../contacts/components/is-active/is-active.component';
import { AvatarComponent } from '../../../../../../../../components/avatar/avatar.component';
import { AssignableParty } from '../../../../../contacts/components/add-department-contact/models/assignable-party';
import {
  AddPersonContactComponent,
  AddPersonContactData,
  AddPersonContactResult
} from '../../../../../contacts/components/add-person-contact/add-person-contact.component';

const CONTACT_DIALOG_CHROME = { panelClass: 'contact-dialog-panel', backdropClass: 'contact-dialog-backdrop' };

// Below this, the inline "add person" panel shows the full unassigned list rather than
// filtering — mirrors AddPersonContactComponent's own MIN_SEARCH_LENGTH convention.
const MIN_SEARCH_LENGTH = 3;

@Component({
  selector: 'app-people-section',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatIcon,
    CdkDrag,
    CdkDropList,
    CdkDragPlaceholder,
    IsActiveComponent,
    AvatarComponent
  ],
  templateUrl: './people-section.component.html',
  styleUrl: './people-section.component.scss'
})
export class PeopleSectionComponent implements OnInit, OnChanges {
  @Input() people: OrgPersonAssignment[] = [];
  // Full contact list fetched once by the parent (organisation-detail) and shared with
  // DepartmentsSectionComponent — filtered to `type === 'Person'` below.
  @Input() contacts: Contact[] = [];
  @Input() organisationParty: AssignableParty | null = null;
  @Input() autoOpenAdd = false;
  @Output() add = new EventEmitter<OrgPersonAssignment>();
  @Output() remove = new EventEmitter<number>();
  @Output() reorder = new EventEmitter<OrgPersonAssignment[]>();
  @Output() update = new EventEmitter<{ contactId: number; patch: Partial<OrgPersonAssignment> }>();

  readonly resolveEffectiveVisibility = resolveEffectiveVisibility;
  readonly searchControl = new FormControl('', { nonNullable: true });

  addOpen = false;
  // getContactForEdit() below is async — without this, a slow response left the suggestion
  // clickable long enough for a second click to add the same contact twice.
  adding = false;
  filteredContacts$: Observable<Contact[]> = of([]);

  private contacts$ = new BehaviorSubject<Contact[]>([]);
  // Nudged from ngOnChanges whenever `people` changes, so filteredContacts$ recomputes its
  // already-assigned exclusion set right away — combineLatest only reacts to contacts$ and the
  // search box on their own, and `people` is a plain @Input, not a stream, so without this a
  // contact just added/removed here would keep showing (or stay hidden) in the dropdown until the
  // admin typed something to make the search box re-emit.
  private assignmentsChanged$ = new BehaviorSubject<void>(undefined);
  private contactsService = inject(ContactsService);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.filteredContacts$ = combineLatest([
      this.contacts$,
      this.searchControl.valueChanges.pipe(startWith('')),
      this.assignmentsChanged$
    ]).pipe(
      map(([contacts, term]) => {
        const assigned = new Set(this.people.map(person => person.contactId));
        const unassignedPeople = contacts.filter(contact => contact.type === 'Person' && !assigned.has(contact.id));
        const value = term.trim().toLowerCase();
        if (value.length < MIN_SEARCH_LENGTH) {
          return unassignedPeople;
        }
        return unassignedPeople.filter(
          contact =>
            contact.displayName.toLowerCase().includes(value) || contact.jobTitle?.toLowerCase().includes(value)
        );
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['autoOpenAdd']?.currentValue) {
      this.addOpen = true;
    }
    if (changes['people']) {
      this.assignmentsChanged$.next();
    }
    if (changes['contacts']) {
      this.contacts$.next(this.contacts);
    }
  }

  trackByContactId(_: number, contact: Contact) {
    return contact.id;
  }

  trackByAssignmentId(_: number, assignment: OrgPersonAssignment) {
    return assignment.contactId;
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<OrgPersonAssignment[]>) {
    const reordered = [...this.people];
    moveItemInArray(reordered, previousIndex, currentIndex);
    this.reorder.emit(reordered);
  }

  selectContact(contact: Contact) {
    if (this.adding) {
      return;
    }
    this.adding = true;
    // Hide the panel immediately rather than waiting on the request below — leaving it open (and
    // the suggestion clickable) while getContactForEdit() is in flight is what let a second click
    // add the same contact twice.
    this.addOpen = false;
    // The search row (Contact) doesn't carry the contact-level visibility defaults or address —
    // only GET /supportContacts/{id} does — so fetch the full record once at pick time to seed
    // an accurate Customer Preview instead of assuming false/null until the next reload.
    this.contactsService
      .getContactForEdit(contact.id)
      .pipe(
        finalize(() => (this.adding = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(detail => {
        this.add.emit({
          contactId: contact.id,
          displayName: contact.displayName,
          email: contact.email,
          jobTitle: contact.jobTitle,
          phone: contact.phone,
          mobilePhone: contact.mobilePhone,
          address: detail.address,
          photoUrl: contact.photoUrl,
          isActive: contact.isActive,
          defaultShowMobilePhone: detail.showMobilePhone,
          defaultShowPhoto: detail.showPhoto,
          title: null,
          sortOrder: this.people.length,
          showMobilePhone: null,
          showPhoto: null
        });
      });
  }

  createNewContact() {
    this.matDialog
      .open<AddPersonContactComponent, AddPersonContactData, AddPersonContactResult>(AddPersonContactComponent, {
        ...CONTACT_DIALOG_CHROME,
        data: this.organisationParty ? { preselectedParty: this.organisationParty } : undefined
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.action === 'Added') {
          const contact = result.contact;
          this.add.emit({
            contactId: Number(contact.id),
            displayName: contact.displayName,
            email: contact.email,
            jobTitle: contact.jobTitle,
            phone: contact.phone,
            mobilePhone: contact.mobilePhone,
            address: contact.address,
            photoUrl: null,
            isActive: contact.isActive,
            defaultShowMobilePhone: contact.showMobilePhone,
            defaultShowPhoto: contact.showPhoto,
            title: null,
            sortOrder: this.people.length,
            showMobilePhone: null,
            showPhoto: null
          });
          this.addOpen = false;
        }
      });
  }

  updateTitle(contactId: number, title: string) {
    this.update.emit({ contactId, patch: { title: title.trim() || null } });
  }

  toggleVisibility(contactId: number, field: 'showMobilePhone' | 'showPhoto', checked: boolean) {
    this.update.emit({ contactId, patch: { [field]: checked } });
  }

  resetVisibility(contactId: number, field: 'showMobilePhone' | 'showPhoto') {
    this.update.emit({ contactId, patch: { [field]: null } });
  }
}
